import { Loader } from "@common/handleErrorAndLoading/Loader";
import { Modal } from "@common/Modal/Modal";
import { SubNavigation } from "@common/Navigation/SubNavigation";
import UploadFileButton from "@common/uploadFileButton/UploadFileButton";
import { TITLE_INHERIT_STYLE, WORDPRESS_URL } from "@constants";
import { useUploadContactFile } from "@features/Contact/hook/useUploadContactFile";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Typography,
} from "@material-ui/core";
import { getLanguage } from "@utils/changeLanguage";
import { Fragment, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ContactReason {
    value: string;
    label: string;
}

const reasonsForContact: ContactReason[] = [
    { value: "OTHER", label: "other" },
    { value: "COMPLAINT", label: "complaint" },
    { value: "DATA_SUBJECT_REQUEST", label: "dataSubjectRequest" },
];

const ContactPage = () => {
    const { t } = useTranslation(["contact", "common", "glossary", "validation"]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { formMethods, handleOnSubmit, uploadContactDocumentState } = useUploadContactFile(() => {
        setIsModalOpen(true);
    });
    const { errors, control, setValue } = formMethods;
    const [reason, setReason] = useState<string>(reasonsForContact[0].value);
    const title = reasonsForContact.find((x) => x.value === reason)?.label;

    useEffect(() => {
        setValue("docType", "OTHER");
    }, []);

    return (
        <Fragment>
            <SubNavigation />
            <div className="container-boxed">
                <Typography align="center" color="secondary" paragraph variant="h1">
                    {t("contact:title")}
                </Typography>
                <Typography align="center" color="secondary" paragraph variant="body1">
                    {t("contact:uploadInfo")}
                </Typography>
                <Card className="select-number">
                    <CardContent className="select-number-content">
                        <form className="select-number_form">
                            <Controller
                                control={control}
                                name={"docType"}
                                render={(renderProps) => (
                                    <Grid container justify="center" spacing={2}>
                                        <Grid item xs={12}>
                                            <InputLabel htmlFor="filled-number">
                                                {t("contact:reasonForContactingUs")}:
                                            </InputLabel>
                                        </Grid>
                                        <Grid item sm={7} xs={9}>
                                            <Select
                                                disableUnderline
                                                displayEmpty
                                                fullWidth
                                                id="filled-number"
                                                inputRef={renderProps.ref}
                                                name={renderProps.name}
                                                value={renderProps.value || reason}
                                                variant="filled"
                                                onChange={(event) => {
                                                    setReason(event.target.value as string);
                                                    setValue("base64File", undefined);

                                                    return renderProps.onChange(event);
                                                }}
                                            >
                                                {reasonsForContact.map((reason) => (
                                                    <MenuItem key={reason.value} value={reason.value}>
                                                        {t(reason.label as any)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                    </Grid>
                                )}
                                rules={{
                                    required: t("validation:required"),
                                }}
                            />
                        </form>
                    </CardContent>
                </Card>
                <Card elevation={2}>
                    <CardHeader
                        className="card-header card-header__right"
                        title={t(`contact:${title}` as any)}
                        titleTypographyProps={TITLE_INHERIT_STYLE}
                    />
                    <CardContent>
                        <div className="card-header__circle" />
                        <form onSubmit={handleOnSubmit}>
                            <Grid alignItems="center" container direction="column" justify="center" spacing={4}>
                                <Grid item>
                                    {reason === "DATA_SUBJECT_REQUEST" && (
                                        <Typography color="secondary" variant="body1">
                                            {t("contact:dataSubjectRequest1")}
                                            <Link
                                                color="inherit"
                                                href={`${WORDPRESS_URL}/${getLanguage()}/privacy/#f447e8e4ad834aba0`} // AC - This might need to be changed
                                                rel="noopener"
                                                target="_blank"
                                                underline="always"
                                            >
                                                {t("contact:privacyPolicy")}
                                            </Link>
                                            {t("contact:dataSubjectRequest2")}
                                            <Link
                                                color="inherit"
                                                href={`${WORDPRESS_URL}/${getLanguage()}/privacy/#05faf9494fd9382e8`} // AC - To be changed and download the form
                                                rel="noopener"
                                                target="_blank"
                                                underline="always"
                                            >
                                                {t("contact:here")}
                                            </Link>
                                        </Typography>
                                    )}
                                    {reason === "COMPLAINT" && (
                                        <Typography color="secondary" variant="body1">
                                            {t("contact:uploadComplaintDocumentation")}
                                            <Link
                                                color="inherit"
                                                href={`${WORDPRESS_URL}/wp-content/uploads/Complaint-Form_${
                                                    getLanguage() === "el" ? "GR_-" : "%CE%95%CE%9D_"
                                                }GoMo.pdf`}
                                                rel="noopener"
                                                target="_blank"
                                                underline="always"
                                            >
                                                {t("contact:here")}
                                            </Link>
                                        </Typography>
                                    )}
                                    {reason === "OTHER" && (
                                        <Typography color="secondary" variant="body1">
                                            {t("contact:uploadOtherDocumentation")}
                                        </Typography>
                                    )}
                                </Grid>
                                {/* <Grid item container spacing={2} direction="row" justify="center" alignItems="center"> */}
                                {/* {reason === "DATA_SUBJECT_REQUEST" && (
                                        <Grid item>
                                            <Typography color="secondary" variant="body1">
                                                {t("contact:uploadForm")}
                                            </Typography>
                                        </Grid>
                                    )} */}
                                <Grid item>
                                    <UploadFileButton
                                        buttonText={
                                            reason === "DATA_SUBJECT_REQUEST"
                                                ? `${t("contact:dataSubjectRequest")} *`
                                                : `${t("contact:upload")} *`
                                        }
                                        control={control}
                                        disabled={uploadContactDocumentState.isLoading}
                                        error={errors.base64File}
                                        name={"base64File"}
                                    />
                                </Grid>
                                {/* </Grid> */}
                                {uploadContactDocumentState.error && (
                                    <Grid item>
                                        <Typography color="error" variant="body1">
                                            {uploadContactDocumentState.error.errorMessage}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid
                                    alignItems="center"
                                    container
                                    direction="row"
                                    item
                                    justify="center"
                                    sm={8}
                                    spacing={2}
                                    xs={12}
                                >
                                    {uploadContactDocumentState.isLoading && <Loader size={40} />}
                                    {!uploadContactDocumentState.isLoading && (
                                        <>
                                            {/* <Grid item xs={4}>
                                                <Button
                                                    color="secondary"
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={handleCancelClick}
                                                >
                                                    {t("common:cancel")}
                                                </Button>
                                            </Grid> */}
                                            <Grid item xs={4}>
                                                <Button
                                                    color="secondary"
                                                    formNoValidate
                                                    fullWidth
                                                    type="submit"
                                                    variant="contained"
                                                >
                                                    {t("glossary:submit")}
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            </div>
            <Modal
                close={() => setIsModalOpen(false)}
                confirm={() => setIsModalOpen(false)}
                confirmLabel={t("glossary:close")}
                content={
                    reason === "DATA_SUBJECT_REQUEST"
                        ? t("contact:documentUploadedSuccessDSR")
                        : t("contact:documentUploadedSuccessOther")
                }
                isModalOpen={isModalOpen}
                title={t("contact:documentUploadedSuccessTitle")}
            />
        </Fragment>
    );
};

export default ContactPage;
